import { selectorPackDocumentSchema } from '@midiautolistening/selector-pack';
import { z } from 'zod';

import { configVersionSchema, idSchema, isoDateTimeSchema, ulidSchema } from '../common';
import {
  agentCommandStatusSchema,
  commentSourceSchema,
  platformSchema,
  platformSessionStatusSchema,
} from '../enums';
import { platformNavigableUrlSchema } from '../platform-catalog';
import { projectConfigSnapshotSchema } from '../rest/projects';
import { selectorPackLocaleSchema } from '../rest/selector-packs';
import { searchPostsBatchAckSchema } from './agent-to-server';
import { defineEventSchema, eventUnion } from './envelope';

/**
 * ███ SERVER → AGENT: THE COMMAND ALLOWLIST ███
 *
 * This union is a security boundary, not a convenience. Everything the cloud is
 * able to make a user's machine do is enumerated here, and the enumeration is
 * closed: the agent parses every inbound frame with `serverToAgentMessageSchema`
 * and drops whatever does not match, so an event name that is not listed below
 * simply cannot be executed — even if a compromised or buggy server sends it.
 *
 * What is deliberately NOT representable in this type space, and must never be
 * added:
 *
 *   - a command that runs a shell command or spawns a process,
 *   - a command that evaluates JavaScript (`page.evaluate` with a server-supplied
 *     string), which would turn the config channel into remote code execution on
 *     every client machine,
 *   - a command that reads, writes or uploads an arbitrary file path,
 *   - a command that navigates the browser to an arbitrary URL — the only URLs
 *     that travel belong to a platform in `PLATFORM_CATALOG` and were resolved
 *     by the server from its own stored rows (see `navigableUrlSchema`),
 *   - a command that exfiltrates cookies, tokens or the browser profile,
 *   - a command that solves, skips or otherwise works around a CAPTCHA,
 *     checkpoint or 2FA prompt. Those states stop automation and ask the human.
 *
 * Adding any of the above violates an architectural invariant of the product.
 * If a future feature seems to need one, the feature is wrong.
 *
 * Every command carries `commandId` (a client-sortable ULID), `type`,
 * `issuedAt`, `expiresAt` and a typed `payload`. The agent is idempotent by
 * `commandId` — re-delivery after a reconnect is a no-op — and MUST drop a
 * command whose `expiresAt` has passed. The expiry is what stops a command
 * queued while an agent was offline from opening a browser window on someone's
 * desktop an hour after the operator asked for it.
 */

/**
 * A URL the agent is allowed to navigate to.
 *
 * Constrained to the https hosts of the platforms in `PLATFORM_CATALOG` at the
 * *contract* level, so "navigate this machine anywhere" is not expressible even
 * by accident. Values only ever come from a permalink the server already stored
 * for a post; a client never supplies one (see `openPostRequestSchema`, which
 * names a post id, not a URL). The agent re-checks the host against the
 * profile's own platform before navigating — this schema is the first of two
 * gates, not the only one.
 */
export const navigableUrlSchema = platformNavigableUrlSchema;

/** @deprecated Facebook-era name — use `navigableUrlSchema`. */
export const facebookUrlSchema = navigableUrlSchema;

/**
 * Build one command message.
 *
 * `event` and `type` hold the same literal. `event` is what the frame router
 * dispatches on, uniformly with every other message in the system; `type` is
 * what gets persisted in `agent_commands`, echoed in `command.ack` and audited.
 * Both are the same `z.literal`, so they cannot disagree on the wire.
 */
function defineCommandSchema<Type extends string, Payload extends z.ZodTypeAny>(
  type: Type,
  payload: Payload,
) {
  return defineEventSchema(type, {
    commandId: ulidSchema,
    type: z.literal(type),
    issuedAt: isoDateTimeSchema,
    /** Hard deadline. After this instant the agent drops the command unexecuted. */
    expiresAt: isoDateTimeSchema,
    payload,
  });
}

// ---------------------------------------------------------------------------
// agent.configuration_updated
// ---------------------------------------------------------------------------

/**
 * The full configuration the agent should be running.
 *
 * Idempotent by the three numbers in `configVersionSchema`: the agent compares
 * them against what it already applied and does nothing when they match, which
 * is what makes re-delivery after every reconnect harmless.
 *
 * It carries no credential of any kind. Browser profiles are referenced by id;
 * the directory, its cookies and the Facebook account behind it never travel in
 * either direction.
 */
export const configurationUpdatedPayloadSchema = configVersionSchema.extend({
  projects: z.array(projectConfigSnapshotSchema).max(200),
  /** Projects this agent must stop and forget — unassigned, deleted, or moved
   *  to another agent. Sending the remaining set is not enough: the agent cannot
   *  distinguish "removed" from "omitted for brevity". */
  removedProjectIds: z.array(idSchema).max(200).default([]),
  /** Workspace-wide kill switch. While true the agent runs no search cycle and
   *  executes no comment, whatever the individual project snapshots say. */
  emergencyStop: z.boolean(),
  heartbeatIntervalSeconds: z.number().int().min(5).max(300),
  /**
   * The browser profiles the server already knows for this agent, with their
   * last recorded session reading. DATA, not a command: nothing here makes the
   * agent open a browser or navigate anywhere — it only lets a freshly
   * restarted agent show what the server (the source of truth) last measured
   * instead of "0/1 đã đăng nhập" about a session that is alive on disk.
   *
   * Display state only, like everything on this channel: no cookie, no storage
   * state, no directory path — `profileDirectoryName` is a NAME under the
   * agent's own profiles folder, the same field `browser.open_login` carries.
   * `.default([])` keeps every frame from an older server valid.
   */
  browserProfiles: z
    .array(
      z.object({
        browserProfileId: idSchema,
        label: z.string().trim().min(1).max(200),
        profileDirectoryName: z.string().trim().min(1).max(200),
        /** Which channel this Chromium profile is signed into. Fixed at
         *  registration; the agent picks login URL, session detectors and
         *  navigation allowlist from it. */
        platform: platformSchema.default('FACEBOOK'),
        sessionStatus: platformSessionStatusSchema,
        /** Null when the server never received a session reading. */
        lastSessionCheckAt: isoDateTimeSchema.nullable(),
        /** Display identity only — never a credential. */
        displayName: z.string().trim().max(200).nullable(),
      }),
    )
    .max(50)
    .default([]),
});
export type ConfigurationUpdatedPayload = z.infer<typeof configurationUpdatedPayloadSchema>;

export const agentConfigurationUpdatedCommandSchema = defineCommandSchema(
  'agent.configuration_updated',
  configurationUpdatedPayloadSchema,
);
export type AgentConfigurationUpdatedCommand = z.infer<
  typeof agentConfigurationUpdatedCommandSchema
>;

// ---------------------------------------------------------------------------
// selector_pack.updated
// ---------------------------------------------------------------------------

/**
 * A newly published SelectorPack version.
 *
 * The document is validated here by the same schema the API used at publish
 * time, and the agent validates it AGAIN on receipt before applying it. The
 * second check is not redundant: it is what keeps a compromised or buggy server
 * from feeding the interpreter something outside its closed vocabularies. The
 * pack is data — locators, conditions and scalar knobs — interpreted by fixed
 * code. It is never executed, and it can never contain a JavaScript string.
 *
 * An agent whose `adapterVersion` is below `minAdapterVersion` must
 * refuse the pack and keep its baseline rather than half-apply it.
 */
export const selectorPackUpdatedPayloadSchema = z.object({
  packId: idSchema,
  /** Which platform's pack this is. The agent stores packs per platform, so a
   *  Facebook publish can never overwrite the Threads pack or vice versa. */
  platform: platformSchema.default('FACEBOOK'),
  /** Monotonic per pack; application is idempotent by this number. */
  selectorPackVersion: z.number().int().min(1),
  locale: selectorPackLocaleSchema,
  minAdapterVersion: z.number().int().min(1),
  document: selectorPackDocumentSchema,
  publishedAt: isoDateTimeSchema,
});
export type SelectorPackUpdatedPayload = z.infer<typeof selectorPackUpdatedPayloadSchema>;

export const selectorPackUpdatedCommandSchema = defineCommandSchema(
  'selector_pack.updated',
  selectorPackUpdatedPayloadSchema,
);
export type SelectorPackUpdatedCommand = z.infer<typeof selectorPackUpdatedCommandSchema>;

// ---------------------------------------------------------------------------
// browser.*
// ---------------------------------------------------------------------------

/**
 * Open a headed window on the Facebook login page so the user can sign in.
 *
 * No URL travels: the agent navigates to the login page its adapter knows. The
 * system never types a Facebook password, never stores one, and never attempts
 * to satisfy a 2FA or checkpoint prompt on the user's behalf — the whole point
 * of this command is to hand control to the human.
 */
/**
 * Name of the Chromium `userDataDir` under the agent's own profiles folder.
 *
 * A NAME, never a path: the pattern excludes `/`, `\` and `.` runs, because this
 * is the one string the server sends that a client turns into a filesystem
 * location. Sending it at all is what lets a Facebook session SURVIVE re-pairing
 * — the directory is otherwise derived from the profile id, so a new profile row
 * meant a new empty Chromium profile and a fresh manual login every time.
 */
const profileDirectoryNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/);

export const browserOpenLoginPayloadSchema = z.object({
  browserProfileId: idSchema,
  profileDirectoryName: profileDirectoryNameSchema.optional(),
  /** Which platform's login page to open — resolved to `loginUrl` from the
   *  catalog on the agent side; no URL travels. Defaulted so a frame from an
   *  older server still parses. */
  platform: platformSchema.default('FACEBOOK'),
  projectId: idSchema.optional(),
  reason: z.string().trim().max(500).optional(),
});
export type BrowserOpenLoginPayload = z.infer<typeof browserOpenLoginPayloadSchema>;

export const browserOpenLoginCommandSchema = defineCommandSchema(
  'browser.open_login',
  browserOpenLoginPayloadSchema,
);
export type BrowserOpenLoginCommand = z.infer<typeof browserOpenLoginCommandSchema>;

/**
 * Close any headless context on this profile and open a HEADED one.
 *
 * This is not a "show window" toggle. Playwright cannot flip `headless` on a
 * live browser, and one `userDataDir` may only be open in a single process, so
 * becoming visible genuinely means closing one context and launching another.
 *
 * `url` is optional and exists for "open this post in Chromium": the server
 * resolves the permalink from its own stored post row before issuing the
 * command. `postId` is echoed so the agent can report back against it.
 */
export const browserOpenInteractivePayloadSchema = z.object({
  browserProfileId: idSchema,
  profileDirectoryName: profileDirectoryNameSchema.optional(),
  postId: idSchema.optional(),
  url: navigableUrlSchema.optional(),
  bringToFront: z.boolean().default(true),
});
export type BrowserOpenInteractivePayload = z.infer<typeof browserOpenInteractivePayloadSchema>;

export const browserOpenInteractiveCommandSchema = defineCommandSchema(
  'browser.open_interactive',
  browserOpenInteractivePayloadSchema,
);
export type BrowserOpenInteractiveCommand = z.infer<typeof browserOpenInteractiveCommandSchema>;

/** Close the headed window; optionally relaunch headless for the assigned project. */
export const browserCloseInteractivePayloadSchema = z.object({
  browserProfileId: idSchema,
  relaunchHeadless: z.boolean().default(false),
});
export type BrowserCloseInteractivePayload = z.infer<typeof browserCloseInteractivePayloadSchema>;

export const browserCloseInteractiveCommandSchema = defineCommandSchema(
  'browser.close_interactive',
  browserCloseInteractivePayloadSchema,
);
export type BrowserCloseInteractiveCommand = z.infer<typeof browserCloseInteractiveCommandSchema>;

/**
 * Move an already-open headed window. Windows-native operations that touch only
 * the window manager — they never change the headless mode and never navigate.
 */
export const browserWindowPayloadSchema = z.object({
  browserProfileId: idSchema,
});
export type BrowserWindowPayload = z.infer<typeof browserWindowPayloadSchema>;

export const browserBringToFrontCommandSchema = defineCommandSchema(
  'browser.bring_to_front',
  browserWindowPayloadSchema,
);
export type BrowserBringToFrontCommand = z.infer<typeof browserBringToFrontCommandSchema>;

export const browserMinimizeCommandSchema = defineCommandSchema(
  'browser.minimize',
  browserWindowPayloadSchema,
);
export type BrowserMinimizeCommand = z.infer<typeof browserMinimizeCommandSchema>;

export const browserStartPayloadSchema = z.object({
  browserProfileId: idSchema,
  profileDirectoryName: profileDirectoryNameSchema.optional(),
  projectId: idSchema.optional(),
  /** Automation runs headless by default; a headed context is only for the human. */
  headless: z.boolean().default(true),
});
export type BrowserStartPayload = z.infer<typeof browserStartPayloadSchema>;

export const browserStartCommandSchema = defineCommandSchema(
  'browser.start',
  browserStartPayloadSchema,
);
export type BrowserStartCommand = z.infer<typeof browserStartCommandSchema>;

export const browserStopPayloadSchema = z.object({
  browserProfileId: idSchema,
  reason: z.string().trim().max(500).optional(),
});
export type BrowserStopPayload = z.infer<typeof browserStopPayloadSchema>;

export const browserStopCommandSchema = defineCommandSchema(
  'browser.stop',
  browserStopPayloadSchema,
);
export type BrowserStopCommand = z.infer<typeof browserStopCommandSchema>;

// ---------------------------------------------------------------------------
// project.*
// ---------------------------------------------------------------------------

/**
 * Start running a project on a profile. The pairing is 1:1 by design — a profile
 * runs at most one project at a time — so the profile id is required here even
 * though the agent also learns it from the configuration snapshot.
 */
export const projectStartPayloadSchema = z.object({
  projectId: idSchema,
  browserProfileId: idSchema,
  /**
   * Same directory the login used.
   *
   * Starting a project launches its own browser, and without this it fell back to
   * the profile id — a DIFFERENT `userDataDir` from the one `browser.open_login`
   * opened. The operator signed in, the cycle then ran in a Chromium profile that
   * had never seen Facebook, and the result read as "SelectorPack needs
   * calibrating" when the page was simply logged out.
   */
  profileDirectoryName: profileDirectoryNameSchema.optional(),
  runImmediately: z.boolean().default(true),
});
export type ProjectStartPayload = z.infer<typeof projectStartPayloadSchema>;

export const projectStartCommandSchema = defineCommandSchema(
  'project.start',
  projectStartPayloadSchema,
);
export type ProjectStartCommand = z.infer<typeof projectStartCommandSchema>;

export const projectPausePayloadSchema = z.object({
  projectId: idSchema,
  /** Filled in automatically when the pause came from a checkpoint or 2FA
   *  detection, so the dashboard can explain why work stopped. */
  reason: z.string().trim().max(500).optional(),
});
export type ProjectPausePayload = z.infer<typeof projectPausePayloadSchema>;

export const projectPauseCommandSchema = defineCommandSchema(
  'project.pause',
  projectPausePayloadSchema,
);
export type ProjectPauseCommand = z.infer<typeof projectPauseCommandSchema>;

export const projectResumePayloadSchema = z.object({
  projectId: idSchema,
});
export type ProjectResumePayload = z.infer<typeof projectResumePayloadSchema>;

export const projectResumeCommandSchema = defineCommandSchema(
  'project.resume',
  projectResumePayloadSchema,
);
export type ProjectResumeCommand = z.infer<typeof projectResumeCommandSchema>;

export const projectStopPayloadSchema = z.object({
  projectId: idSchema,
  reason: z.string().trim().max(500).optional(),
});
export type ProjectStopPayload = z.infer<typeof projectStopPayloadSchema>;

export const projectStopCommandSchema = defineCommandSchema(
  'project.stop',
  projectStopPayloadSchema,
);
export type ProjectStopCommand = z.infer<typeof projectStopCommandSchema>;

// ---------------------------------------------------------------------------
// search.run_once
// ---------------------------------------------------------------------------

export const searchRunOncePayloadSchema = z.object({
  projectId: idSchema,
  /**
   * The profile to run on, and the SAME userDataDir the operator logged into.
   *
   * Both are REQUIRED, not optional. This is the command the scan scheduler
   * issues every `searchIntervalMinutes`, and it is the sole issuer. Making
   * `profileDirectoryName` optional would re-open a real bug: absent it the agent
   * derives the directory from the profile id and opens a DIFFERENT, logged-out
   * Chromium profile, so every cycle returns zero cards while the page is simply
   * signed out. `BrowserProfile.profileDirectoryName` is non-null, so the server
   * always has it — there is no reason to permit its absence.
   */
  browserProfileId: idSchema,
  profileDirectoryName: profileDirectoryNameSchema,
  /** Empty means every active keyword of the project. Ids, not terms: the agent
   *  must not be able to search for something the project never configured. */
  keywordIds: z.array(idSchema).max(200).default([]),
  maxResultsPerKeyword: z.number().int().min(1).max(500).optional(),
});
export type SearchRunOncePayload = z.infer<typeof searchRunOncePayloadSchema>;

export const searchRunOnceCommandSchema = defineCommandSchema(
  'search.run_once',
  searchRunOncePayloadSchema,
);
export type SearchRunOnceCommand = z.infer<typeof searchRunOnceCommandSchema>;

// ---------------------------------------------------------------------------
// comment.execute
// ---------------------------------------------------------------------------

/**
 * Post one comment that has already been generated, validated and approved
 * server-side.
 *
 * The content is final text. There is no template to render and no model to call
 * on the client, so nothing the agent types can differ from what the operator
 * approved.
 *
 * Two literal `true` flags encode invariants that the server must not be able to
 * switch off. They are `z.literal(true)` rather than `z.boolean()` precisely so
 * that "just send false this once" is a schema violation rather than a code
 * review argument.
 */
export const commentExecutePayloadSchema = z.object({
  commentJobId: idSchema,
  projectId: idSchema,
  browserProfileId: idSchema,
  postId: idSchema,
  /** Resolved server-side from the stored post row; constrained to the hosts of
   *  the platforms in the catalog. */
  postUrl: navigableUrlSchema,
  content: z.string().min(1).max(2_000),
  /** Short-lived signed GET URLs for template images in object storage. The
   *  agent fetches them read-only, enforces a size cap and treats the bytes as
   *  an image attachment — they are never written outside its cache directory. */
  mediaUrls: z.array(z.string().url().max(2_048)).max(4).default([]),
  source: commentSourceSchema,
  /** The agent's local guard (recent-comment check, daily counter, correct
   *  profile, FB warning detection) always runs. When it refuses, its refusal
   *  wins over the server's decision. */
  enforceLocalGuard: z.literal(true),
  /** The agent re-checks that the post still exists and is still eligible
   *  immediately before typing, because approval may be minutes old. */
  verifyPostBeforeSend: z.literal(true),
  /** Minimum spacing the agent enforces locally in addition to the server-side
   *  Redis limiter — both sides rate limit, as the spec requires. */
  minIntervalSeconds: z.number().int().min(0).max(86_400),
});
export type CommentExecutePayload = z.infer<typeof commentExecutePayloadSchema>;

export const commentExecuteCommandSchema = defineCommandSchema(
  'comment.execute',
  commentExecutePayloadSchema,
);
export type CommentExecuteCommand = z.infer<typeof commentExecuteCommandSchema>;

// ---------------------------------------------------------------------------
// agent.update_required
// ---------------------------------------------------------------------------

/**
 * The agent build is too old for the workspace's adapter or pack.
 *
 * No download URL travels. The updater's provider (GitHub Releases or S3) is
 * baked into the signed build, so this command can ask the agent to update but
 * cannot point it at a binary of the server's choosing — a server-supplied
 * download URL would be a remote code execution path wearing a helpful hat.
 */
export const agentUpdateRequiredPayloadSchema = z.object({
  requiredAppVersion: z.string().trim().min(1).max(40),
  requiredAdapterVersion: z.number().int().min(1),
  /** When true the server stops accepting work from this agent after the grace
   *  period; when false the update is merely offered. */
  mandatory: z.boolean(),
  reason: z.string().trim().max(500),
  gracePeriodEndsAt: isoDateTimeSchema.nullable(),
});
export type AgentUpdateRequiredPayload = z.infer<typeof agentUpdateRequiredPayloadSchema>;

export const agentUpdateRequiredCommandSchema = defineCommandSchema(
  'agent.update_required',
  agentUpdateRequiredPayloadSchema,
);
export type AgentUpdateRequiredCommand = z.infer<typeof agentUpdateRequiredCommandSchema>;

// ---------------------------------------------------------------------------
// The command union — the allowlist itself
// ---------------------------------------------------------------------------

/**
 * The sixteen commands, and nothing else. Parsing an inbound frame with this
 * union IS the allowlist check on the agent side; the server refuses to persist
 * or dispatch anything it cannot build from these schemas.
 *
 * The runtime list the agent's dispatcher switches on lives in
 * `@midiautolistening/automation-types` (`AGENT_COMMAND_TYPES`). The two must
 * name exactly the same sixteen types — that package cannot depend on this one,
 * so the agreement is maintained by review, and both files say so.
 */
export const serverToAgentCommandSchema = eventUnion([
  agentConfigurationUpdatedCommandSchema,
  selectorPackUpdatedCommandSchema,
  browserOpenLoginCommandSchema,
  browserOpenInteractiveCommandSchema,
  browserCloseInteractiveCommandSchema,
  browserBringToFrontCommandSchema,
  browserMinimizeCommandSchema,
  browserStartCommandSchema,
  browserStopCommandSchema,
  projectStartCommandSchema,
  projectPauseCommandSchema,
  projectResumeCommandSchema,
  projectStopCommandSchema,
  searchRunOnceCommandSchema,
  commentExecuteCommandSchema,
  agentUpdateRequiredCommandSchema,
]);
export type ServerToAgentCommand = z.infer<typeof serverToAgentCommandSchema>;
export type ServerToAgentCommandType = ServerToAgentCommand['type'];

// ---------------------------------------------------------------------------
// Non-command server messages
// ---------------------------------------------------------------------------

/**
 * The server has recorded a command's state — delivered, acknowledged, completed,
 * rejected or expired.
 *
 * This is bookkeeping, not an instruction: it lets the agent retire a command
 * from its local queue. Because execution is idempotent by `commandId`, a
 * duplicate or late ack is a no-op.
 */
export const commandAckSchema = defineEventSchema('command.ack', {
  commandId: ulidSchema,
  status: agentCommandStatusSchema,
  receivedAt: isoDateTimeSchema,
  message: z.string().trim().max(500).optional(),
});
export type CommandAck = z.infer<typeof commandAckSchema>;

/**
 * The server withdraws a command it already issued. "Do less", never "do more".
 *
 * DELIBERATELY NOT A COMMAND, and that placement is the security argument. It
 * sits beside `command.ack` in the non-command half of this file, so it never
 * enters `serverToAgentCommandSchema`, never needs a name in
 * `AGENT_COMMAND_TYPES`, and grants the server no capability whatsoever over the
 * client machine: the only effect it can have is to stop work the server itself
 * had already asked for. There is no payload, no URL and no id but the one
 * command being withdrawn — a revocation that could name work is a command
 * wearing a helpful hat, which is exactly what the allowlist header forbids.
 *
 * WHAT IT IS FOR. `POST /comment-jobs/:id/cancel` is live for a job in
 * SENT_TO_AGENT, i.e. after `comment.execute` has reached the operator's own
 * machine. Until this frame existed, cancelling wrote CANCELLED to a row and the
 * agent typed the comment onto a stranger's post anyway, while the operator read
 * a green "Đã huỷ job" and stopped watching.
 *
 * WHAT IT IS NOT. It is not a guarantee and the server does not report it as
 * one — see `COMMENT_CANCEL_RECALL_OUTCOMES`. Two things can beat it: the agent
 * may be offline (the frame is simply dropped), or it may already be inside the
 * `comment.execute` handler, which awaits an entire typing run.
 *
 * THE AGENT SIDE MUST HANDLE THIS OUT OF BAND. `ws-client.ts` serialises command
 * execution through `commandChain`, so a recall queued onto that chain would be
 * processed *after* the comment it exists to stop — the identical defect that
 * forced the workspace kill switch to jump the queue in the same file. Treat it
 * like `emergencyStop`: act on receipt, do not await the chain. Dropping the
 * `commandId` from the local executed-commands set is not enough on its own,
 * because the frame may arrive while the command is already running.
 */
export const commandRevokedSchema = defineEventSchema('command.revoked', {
  commandId: ulidSchema,
  revokedAt: isoDateTimeSchema,
  /** Vietnamese; for the agent's own log, so a user can see why it stopped. */
  reason: z.string().trim().max(500).optional(),
});
export type CommandRevoked = z.infer<typeof commandRevokedSchema>;

/**
 * Everything an agent may receive.
 *
 * The acks and the revocation sit *outside* `serverToAgentCommandSchema` on
 * purpose: code that means "a command" — persisting to `agent_commands`,
 * enforcing `expiresAt`, writing an audit log entry — then cannot accidentally
 * accept one of them, because none of them type-checks as a command. It also
 * keeps the allowlist honestly countable: the sixteen commands are still
 * sixteen, and nothing here widened what the server may ask a client to do.
 */
export const serverToAgentMessageSchema = z.union([
  serverToAgentCommandSchema,
  searchPostsBatchAckSchema,
  commandAckSchema,
  commandRevokedSchema,
]);
export type ServerToAgentMessage = z.infer<typeof serverToAgentMessageSchema>;
export type ServerToAgentEvent = ServerToAgentMessage['event'];
